import { List } from 'immutable'
import React, { ReactElement, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { usePrevious } from 'react-use'

import { addAlertSignature } from '../../../actions/alerts'
import paths from '../../../constants/paths'
import { CostCenterAccounting } from '../../../model/accountingIntegration'
import Company from '../../../model/company'
import CostCenter from '../../../model/costCenter'
import Employee from '../../../model/employee'
import { OneTimePayCreationFields, OneTimePayMutableFields } from '../../../model/oneTimePay'
import SalaryCycle from '../../../model/salaryCycle'
import { OneTimePayReducer } from '../../../reducers/oneTimePays'
import { formatDate } from '../../../utils/date-utils'
import { formatCurrency, formatInputNumber } from '../../../utils/number-utils'
import { t } from '../../../utils/translation-utils'
import Button from '../../elements/button'
import Card from '../../elements/card'
import Icon from '../../elements/icon'
import Modal from '../../elements/modal'
import Table from '../../elements/table'
import Title from '../../elements/Title'
import TitleMenu from '../../elements/TitleMenu'
import Tooltip from '../../elements/tooltip'
import DumbLink from '../../widgets/DumbLink'
import LoadingOverlay from '../../widgets/LoadingOverlay'
import NoContractCard from '../NoContractCard'
import NoEmploymentCard from '../NoEmploymentCard'
import SalaryReductionEdit from './SalaryReductionEdit'

type Props = {
  subsection?: string
  employee: Employee
  isFreelancer: boolean
  company: Company
  canEditObjects: boolean
  canApproveObjects: boolean
  oneTimePays: OneTimePayReducer
  salaryCycle?: SalaryCycle
  costCenters: List<CostCenter>
  costCenterAccounting: CostCenterAccounting

  addAlert: addAlertSignature
  approveOneTimePays: (ids: string[]) => void
  unapproveOneTimePays: (ids: string[]) => void
  addOneTimePay: (employeeID: string, oneTimePay: OneTimePayCreationFields) => void
  updateOneTimePay: (employeeID: string, oneTimePay: OneTimePayMutableFields) => void
  deleteOneTimePay: (id: string) => void
}

export default function SalaryReductionTab(props: Props): ReactElement | null {
  const [approving, setApproving] = useState<string[]>([])
  const [deleting, setDeleting] = useState<string[]>([])
  const [modalKey, setModalKey] = useState(1)
  const [editing, setEditing] = useState<string | boolean>(false)
  const [mutable, setMutable] = useState(false)

  const setEditVisibility = (id: string | boolean, mutable = true) => {
    // Increment modalKey to create a new component
    setModalKey((prev) => prev + 1)
    setEditing(id)
    setMutable(mutable)
  }

  const { oneTimePays } = props
  const previousOneTimePays = usePrevious(oneTimePays)

  useEffect(() => {
    // Check for save callback
    if (previousOneTimePays && previousOneTimePays.saving && !oneTimePays.saving) {
      // Check for no error occurred
      if (!oneTimePays.error) {
        // Close edit modal
        setEditVisibility(false)
      }
    }
  })

  const showHistory = () => {
    return props.subsection === 'history'
  }

  const approve = (oneTimePayID: string) => {
    return (e: React.MouseEvent) => {
      e.preventDefault()
      setApproving((prev) => [...prev, oneTimePayID])
      props.approveOneTimePays([oneTimePayID])
    }
  }
  const unapprove = (oneTimePayID: string) => {
    return (e: React.MouseEvent) => {
      e.preventDefault()
      setApproving((prev) => prev.filter((id) => id !== oneTimePayID))
      props.unapproveOneTimePays([oneTimePayID])
    }
  }
  const remove = (oneTimePayID: string) => {
    return (e: React.MouseEvent) => {
      e.preventDefault()
      if (window.confirm(t('common.are_you_sure'))) {
        setDeleting((prev) => [...prev, oneTimePayID])
        props.deleteOneTimePay(oneTimePayID)
      }
    }
  }

  type OneTimePayRow = {
    key: string
    id: string
    date: string
    title: string
    amount: string
    rate?: string
    units?: string
    incomeType: string
    approved: boolean
    immutable: boolean
  }

  const columns = [
    {
      title: t('salary_reduction.tab.table.header.approved'),
      key: 'x1',
      className: 'one-time-pay-table-approved',
      render: (oneTimePay: OneTimePayRow) => {
        if (oneTimePay.approved) {
          return (
            <span className="table-approve-wrapper">
              {t('salary_reduction.tab.table.approved.true')}
              {!oneTimePay.immutable && props.canApproveObjects && (
                <Tooltip title={t('salary_reduction.tab.table.approved.remove')}>
                  <span onClick={unapprove(oneTimePay.id)} style={{ cursor: 'pointer' }}>
                    <Icon type="xSign" />
                  </span>
                </Tooltip>
              )}
            </span>
          )
        }
        if (approving.indexOf(oneTimePay.id) !== -1) {
          return t('salary_reduction.tab.table.saving')
        }
        if (!props.canApproveObjects) {
          return t('salary_reduction.tab.table.approved.false')
        }
        return (
          <DumbLink onClick={approve(oneTimePay.id)} type="standard">
            {t('salary_reduction.tab.table.approved.approve')}
          </DumbLink>
        )
      },
    },
    { title: t('salary_reduction.tab.table.header.date'), dataIndex: 'date', key: 'date' },
    { title: t('salary_reduction.tab.table.header.title'), dataIndex: 'title', key: 'title' },
    {
      title: t('salary_reduction.tab.table.header.amount'),
      key: 'amount',
      render: (oneTimePay: OneTimePayRow) => {
        let amount: string | React.ReactNode = oneTimePay.amount
        if (oneTimePay.rate && oneTimePay.units) {
          amount = (
            <span>
              {oneTimePay.amount}
              <br />
              <small>
                ({t('salary_reduction.tab.table.amount.format', { units: oneTimePay.units, rate: oneTimePay.rate })})
              </small>
            </span>
          )
        }
        if (props.isFreelancer) {
          return amount
        }
        return (
          <span>
            {amount}
            <br />
            <span style={{ fontSize: '12px', fontWeight: 'normal' }}>({oneTimePay.incomeType})</span>
          </span>
        )
      },
    },
    {
      title: '',
      key: 'x2',
      className: 'employee-table-actions',
      render: (oneTimePay: OneTimePayRow) => {
        if (deleting.indexOf(oneTimePay.id) !== -1) {
          return null
        }
        if (!props.canEditObjects) {
          return null
        }
        return (
          <div>
            <Tooltip
              title={
                oneTimePay.immutable
                  ? t('salary_reduction.tab.table.actions.view')
                  : t('salary_reduction.tab.table.actions.edit')
              }
            >
              <span
                onClick={() => setEditVisibility(oneTimePay.id, !oneTimePay.immutable)}
                style={{ cursor: 'pointer' }}
              >
                <Icon type={oneTimePay.immutable ? 'magnifyingGlass' : 'paperWithPencil'} />
              </span>
            </Tooltip>
            {!oneTimePay.immutable && (
              <Tooltip title={t('salary_reduction.tab.table.actions.delete')}>
                <span onClick={remove(oneTimePay.id)} style={{ cursor: 'pointer' }}>
                  <Icon type="xSign" />
                </span>
              </Tooltip>
            )}
          </div>
        )
      },
    },
  ]

  const getSalaryReductions = (): OneTimePayRow[] => {
    return props.oneTimePays.oneTimePays
      .filter(
        (oneTimePay) =>
          oneTimePay.type === 'Gross Pay Reduction' ||
          oneTimePay.type === 'Gross Pay Reduction No Vacation' ||
          oneTimePay.type === 'Gross Pay Reduction No Pension' ||
          oneTimePay.type === 'Gross Pay Reduction No Vacation and Pension' ||
          oneTimePay.type === 'B Income Reduction' ||
          oneTimePay.type === 'B Income Reduction No AM'
      )
      .filter((oneTimePay) => (showHistory() ? oneTimePay.settled : !oneTimePay.settled))
      .map((oneTimePay) => {
        let title = oneTimePay.title
        if (title.length > 22) {
          title = title.substring(0, 20) + '...'
        }
        let incomeType = t('salary_reduction.tab.table.income_type.a')
        if (oneTimePay.type === 'B Income Reduction' || oneTimePay.type === 'B Income Reduction No AM') {
          incomeType = t('salary_reduction.tab.table.income_type.b')
        }
        return {
          key: oneTimePay.id,
          id: oneTimePay.id,
          date: formatDate(oneTimePay.dispositionDate),
          amount: formatCurrency(oneTimePay.amount, 2),
          rate: oneTimePay.rate ? formatCurrency(oneTimePay.rate, 2) : undefined,
          units: oneTimePay.units ? formatInputNumber(oneTimePay.units) : undefined,
          title,
          incomeType,
          immutable: oneTimePay.immutable,
          approved: oneTimePay.approved,
        }
      })
      .toArray()
  }

  const approveAll = (e: React.MouseEvent) => {
    e.preventDefault()
    if (window.confirm(t('common.are_you_sure'))) {
      const oneTimePays = getSalaryReductions().filter((reg) => !reg.approved)
      setApproving(oneTimePays.map((reg) => reg.id))
      props.approveOneTimePays(oneTimePays.map((otp) => otp.id))
    }
  }

  const hasUnapprovedSalaryReductions = () => {
    return getSalaryReductions().some((otp) => !otp.approved)
  }

  if (!props.employee.activeEmployment) {
    return <NoEmploymentCard />
  }
  if (
    !props.employee.activeContract ||
    props.employee.activeEmployment.id !== props.employee.activeContract.employmentID
  ) {
    return <NoContractCard employee={props.employee} />
  }
  if (!props.salaryCycle) {
    return null
  }

  return (
    <Card className="employees-single-form">
      <TitleMenu>
        {!showHistory() && props.canEditObjects && (
          <Button onClick={() => setEditVisibility(true)} style={{ marginRight: 20 }} prefixIcon="user">
            {t('salary_reduction.tab.header.add_new')}
          </Button>
        )}
        {showHistory() ? (
          <Link
            to={
              '/' +
              (props.isFreelancer ? paths.FREELANCERS : paths.EMPLOYEES) +
              '/' +
              props.employee.id +
              '/salary-reduction'
            }
          >
            <Button className="gtm-hide-salary-reduction-history" style={{ paddingRight: 14 }}>
              {t('salary_reduction.tab.header.hide_history')}
            </Button>
          </Link>
        ) : (
          <Link
            to={
              '/' +
              (props.isFreelancer ? paths.FREELANCERS : paths.EMPLOYEES) +
              '/' +
              props.employee.id +
              '/salary-reduction/history'
            }
          >
            <Button className="gtm-show-salary-reduction-history" style={{ paddingRight: 14 }}>
              {t('salary_reduction.tab.header.show_history')}
            </Button>
          </Link>
        )}

        {hasUnapprovedSalaryReductions() && props.canApproveObjects && (
          <Button onClick={approveAll} style={{ marginLeft: 20 }}>
            {t('salary_reduction.tab.header.approve_all')}
          </Button>
        )}
      </TitleMenu>
      <Title>
        {showHistory() ? t('salary_reduction.tab.title.history') : t('salary_reduction.tab.title.standard')}
      </Title>
      <p>&nbsp;</p>

      <Table columns={columns} dataSource={getSalaryReductions()} pagination={false} />
      {props.oneTimePays.saving && <LoadingOverlay />}

      <Modal
        key={modalKey}
        visible={editing !== false}
        onOk={() => setEditVisibility(false)}
        onCancel={() => setEditVisibility(false)}
        width={582}
        footer={null}
      >
        <SalaryReductionEdit
          visible={editing !== false}
          editing={mutable}
          canApproveObjects={props.canApproveObjects}
          employee={props.employee}
          isFreelancer={props.isFreelancer}
          company={props.company}
          oneTimePayID={typeof editing === 'string' ? editing : undefined}
          oneTimePays={props.oneTimePays}
          salaryCycle={props.salaryCycle}
          costCenters={props.costCenters}
          costCenterAccounting={props.costCenterAccounting}
          addOneTimePay={props.addOneTimePay}
          updateOneTimePay={props.updateOneTimePay}
        />
      </Modal>
    </Card>
  )
}
